import { useCallback, useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../../config/firebaseConfig';
import { SHIPMENT_STATUS, todayInputValue } from '../../../utils/shipments';
import {
  COMPANY_SHIPMENTS_COLLECTION,
  deleteCompanyShipment,
  saveCompanyShipment,
} from '../../../utils/companyShipments';

const sortTime = (shipment) =>
  new Date(shipment.createdAt || shipment.shippedAt || 0).getTime() || 0;

// Durum değişince tarihler otomatik dolar (form ve hızlı işlem aynı kuralı kullanır)
export const datesForStatus = (shipment, status) => {
  const dates = {};
  if (status !== SHIPMENT_STATUS.NOT_SENT && !shipment.shippedAt) {
    dates.shippedAt = todayInputValue();
  }
  if (status === SHIPMENT_STATUS.DELIVERED && !shipment.deliveredAt) {
    dates.deliveredAt = todayInputValue();
  }
  return dates;
};

/**
 * Firma kargolarını canlı dinler ve yazma işlemlerini sunar.
 * `ownerId` verilirse yalnızca o çalışanın kayıtları gelir (Profil görünümü).
 */
const useCompanyShipments = ({ ownerId = null, actorName = '' } = {}) => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const base = collection(db, COMPANY_SHIPMENTS_COLLECTION);
    const source = ownerId ? query(base, where('ownerId', '==', ownerId)) : base;

    setLoading(true);
    return onSnapshot(
      source,
      (snapshot) => {
        const rows = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
        rows.sort((a, b) => sortTime(b) - sortTime(a));
        setShipments(rows);
        setError(null);
        setLoading(false);
      },
      (snapshotError) => {
        console.error('Kargo kayıtları yüklenemedi:', snapshotError);
        setError('Kargo kayıtları yüklenemedi. Lütfen sayfayı yenileyin.');
        setLoading(false);
      }
    );
  }, [ownerId]);

  const historyEntry = useCallback(
    (status) => ({ status, at: new Date().toISOString(), by: actorName }),
    [actorName]
  );

  // Yeni kayıt (existing = null) veya güncelleme
  const saveShipment = useCallback(
    (existing, formData, owner) => {
      if (!existing) {
        return saveCompanyShipment(null, {
          ...formData,
          ownerId: owner?.id || '',
          ownerName: owner?.name || actorName,
          createdAt: new Date().toISOString(),
          createdBy: actorName,
          history: [historyEntry(formData.status)],
        });
      }
      const statusChanged = existing.status !== formData.status;
      return saveCompanyShipment(existing.id, {
        ...formData,
        history: statusChanged
          ? [...(existing.history || []), historyEntry(formData.status)]
          : existing.history || [],
      });
    },
    [actorName, historyEntry]
  );

  const changeStatus = useCallback(
    (shipment, status) =>
      saveCompanyShipment(shipment.id, {
        status,
        ...datesForStatus(shipment, status),
        history: [...(shipment.history || []), historyEntry(status)],
      }),
    [historyEntry]
  );

  const removeShipment = useCallback((shipment) => deleteCompanyShipment(shipment.id), []);

  return { shipments, loading, error, saveShipment, changeStatus, removeShipment };
};

export default useCompanyShipments;
